// Signing out, from the account menu and from "Your account": it deletes every
// business's data on this device, so changes that never reached the server are
// counted first and the person is asked.
import { ConfirmDialog, useToast } from '@ims/ui'
import { type ReactNode, useState } from 'react'
import { errorMessage } from '../hooks.ts'
import { readUnsynced } from '../lib/offline.ts'
import { useUser } from '../session.tsx'
import { useStore, useWorkspace } from '../workspace.tsx'

/** `start()` begins signing out; render `dialog` once, wherever the caller draws. */
export function useSignOut(): { start: () => Promise<void>; busy: boolean; dialog: ReactNode } {
  const user = useUser()
  const { current, memberships, signOut } = useWorkspace()
  const { engine } = useStore()
  const toast = useToast()
  const [confirm, setConfirm] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const finish = async () => {
    setBusy(true)
    try {
      await signOut()
    } catch (error) {
      toast.error(errorMessage(error))
      setBusy(false)
    }
  }
  // Sign-out deletes every business's data on this device: count them all.
  const start = async () => {
    const here = await engine.unsyncedCount()
    const counts = [
      ...(here > 0 ? [{ name: current?.tenant.name ?? 'This business', n: here }] : []),
      ...memberships
        .filter((m) => m.tenant_id !== current?.tenant_id)
        .map((m) => ({ name: m.tenant.name, n: readUnsynced(user.id, m.tenant_id) }))
        .filter((c) => c.n > 0),
    ]
    if (counts.length === 0) return finish()
    setConfirm(counts.map((c) => `${c.name}: ${c.n} change${c.n === 1 ? '' : 's'}`).join('; '))
  }

  const dialog = (
    <ConfirmDialog
      open={confirm !== null}
      title="Sign out with unsynced changes?"
      message={`Not on the server yet (${confirm}). Signing out deletes this device’s data, so they would be lost. Connect to the internet and open each business to let them sync first.`}
      confirmLabel="Sign out anyway"
      danger
      busy={busy}
      onClose={() => setConfirm(null)}
      onConfirm={() => void finish()}
    />
  )
  return { start, busy, dialog }
}
